import React from "react";
import {
  useAsyncList,
  AtlasSelect,
  AtlasAsyncSelect,
  AtlasSelectItem,
  Box,
  Text,
} from "@fuegokit/react";
import {
  AkChevronDownIcon,
  AkOfficeBuildingIcon,
} from "@fuegokit/fuegoicons-react";

import { citiesWithKeys, mathOperators } from "./example-data";

export const BasicSelectExample = () => {
  const [domLoaded, setDomLoaded] = React.useState(false);

  React.useEffect(() => {
    setDomLoaded(true);
  }, []);

  return (
    <Box sx={{ py: 4, fontSize: 3, maxWidth: "308px" }}>
      {domLoaded && (
        <AtlasSelect
          appearance="default"
          label="What city do you live in?"
          placeholder="Choose a city"
          loaderPosition="trailing"
          trailingVisual={AkChevronDownIcon}
          isLoading={false}
          isDisabled={false}
          isClearable={false}
          isInvalid={false}
          items={[
            { id: "1", label: "Adelaide", value: "adelaide", extra: "extra" },
            { id: "2", label: "Brisbane", value: "brisbane" },
            { id: "3", label: "Canberra", value: "canberra" },
            { id: "4", label: "Darwin", value: "darwin" },
            { id: "5", label: "Hobart", value: "hobart" },
            { id: "6", label: "Melbourne", value: "melbourne" },
            { id: "7", label: "Perth", value: "perth" },
            { id: "8", label: "Sydney", value: "sydney" },
          ]}
        />
      )}
    </Box>
  );
};

export const CustomTrailingVisualExample = () => {
  const [domLoaded, setDomLoaded] = React.useState(false);

  React.useEffect(() => {
    setDomLoaded(true);
  }, []);

  return (
    <Box sx={{ py: 4, fontSize: 3, maxWidth: "308px" }}>
      {domLoaded && (
        <AtlasSelect
          appearance="default"
          label="What city do you live in?"
          placeholder="Choose a city"
          trailingVisual={AkOfficeBuildingIcon}
          isTrailingVisualAnimated={false}
          items={[
            { id: "1", label: "Adelaide", value: "adelaide", extra: "extra" },
            { id: "2", label: "Brisbane", value: "brisbane" },
            { id: "3", label: "Canberra", value: "canberra" },
            { id: "4", label: "Darwin", value: "darwin" },
            { id: "5", label: "Hobart", value: "hobart" },
            { id: "6", label: "Melbourne", value: "melbourne" },
            { id: "7", label: "Perth", value: "perth" },
            { id: "8", label: "Sydney", value: "sydney" },
          ]}
        />
      )}
    </Box>
  );
};

export const DisabledItemsExample = () => {
  const [domLoaded, setDomLoaded] = React.useState(false);

  React.useEffect(() => {
    setDomLoaded(true);
  }, []);

  return (
    <Box sx={{ py: 4, fontSize: 3, maxWidth: "308px" }}>
      {domLoaded && (
        <AtlasSelect
          disabledItems={["3", "5"]}
          appearance="default"
          label="What city do you live in?"
          placeholder="Choose a city"
          trailingVisual={AkChevronDownIcon}
          isLoading={false}
          isDisabled={false}
          isClearable={false}
          items={citiesWithKeys}
        />
      )}
    </Box>
  );
};

export const ItemsWithCustomLeadingVisualExample = () => {
  const [domLoaded, setDomLoaded] = React.useState(false);

  React.useEffect(() => {
    setDomLoaded(true);
  }, []);

  return (
    <Box sx={{ py: 4, fontSize: 3, maxWidth: "308px" }}>
      {domLoaded && (
        <AtlasSelect
          items={mathOperators}
          label={"Math operators"}
          placeholder={"Choose an operator"}
          trailingVisual={AkChevronDownIcon}
        />
      )}
    </Box>
  );
};

export const BasicSelectWithAsyncLoadingExample = () => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [domLoaded, setDomLoaded] = React.useState(false);

  React.useEffect(() => {
    setDomLoaded(true);
  }, []);

  const list = useAsyncList({
    async load({ signal, filterText }) {
      setIsLoading(true);
      // eslint-disable-next-line no-promise-executor-return
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const res = await fetch(
          `https://swapi.py4e.com/api/people/?search=${filterText}`,
          { signal }
        );
        const json = await res.json();
        return {
          items: json.results,
        };
      } catch (error) {
        console.error("Error loading data:", error);
        return {
          items: [],
        };
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <Box sx={{ py: 4, fontSize: 3, maxWidth: "308px" }}>
      {domLoaded && (
        <AtlasSelect
          appearance="default"
          label="Async loading"
          placeholder="Choose a Star Wars character"
          isClearable={false}
          isLoading={isLoading}
          items={list.items}
          inputValue={list.filterText}
          onInputChange={list.setFilterText}
          loadingState={list.loadingState}
        >
          {(item) => {
            return (
              <AtlasSelectItem
                key={item.id}
                id={crypto.randomUUID()}
                textValue={item.name}
                label={item.name}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Text>{item.name}</Text>
                </Box>
              </AtlasSelectItem>
            );
          }}
        </AtlasSelect>
      )}
    </Box>
  );
};

export const starWarsAPIAsyncDataHandler = async ({ signal, filterText }) => {
  try {
    // Simulate a delay of 1 second
    // eslint-disable-next-line no-promise-executor-return
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Fetch data from the API
    const res = await fetch(
      `https://swapi.dev/api/people/?search=${filterText}`,
      { signal }
    );
    if (!res.ok) {
      throw new Error(`Failed to fetch data: ${res.status} ${res.statusText}`);
    }
    const json = await res.json();
    // Generate unique identifiers for each option, this endpoint doesn't include an `id`
    const itemsWithIds = json.results.map((item, index) => ({
      ...item,
      id: `swapi-${index}`, // Generate unique id based on index
      label: item.name, // Add a label based on OptionType
    }));
    return { items: itemsWithIds };
  } catch (error) {
    console.error("Error loading data:", error);
    return { items: [] }; // Return empty array or handle error accordingly
  }
};

export const AsyncSelectExample = () => {
  const [domLoaded, setDomLoaded] = React.useState(false);

  React.useEffect(() => {
    setDomLoaded(true);
  }, []);
  return (
    <Box sx={{ py: 4, fontSize: 3, maxWidth: "308px" }}>
      {domLoaded && (
        <AtlasAsyncSelect
          appearance="default"
          asyncLoadHandler={starWarsAPIAsyncDataHandler}
          label="Use your own API endpoints"
          placeholder="Choose a Star Wars character"
          loaderPosition="trailing"
          trailingVisual={AkChevronDownIcon}
          isDisabled={false}
          isClearable={false}
          isInvalid={false}
        />
      )}
    </Box>
  );
};

export const HelperTextAndValidationExample = () => {
  const [animalId, setAnimalId] = React.useState(null);
  const [domLoaded, setDomLoaded] = React.useState(false);

  React.useEffect(() => {
    setDomLoaded(true);
  }, []);

  const animalOptions = [
    { id: 1, name: "Aardvark", label: "Aardvark" },
    { id: 2, name: "Cat", label: "Cat" },
    { id: 3, name: "Dog", label: "Dog" },
    { id: 4, name: "Kangaroo", label: "Kangaroo" },
    { id: 5, name: "Koala", label: "Koala" },
    { id: 6, name: "Penguin", label: "Penguin" },
    { id: 7, name: "Snake", label: "Snake" },
    { id: 8, name: "Turtle", label: "Turtle" },
    { id: 9, name: "Wombat", label: "Wombat" },
  ];
  const isValid = React.useMemo(
    () => animalId !== 2 && animalId !== 7,
    [animalId]
  );
  let validationStateValue;
  if (!animalId) {
    validationStateValue = undefined;
  } else if (isValid) {
    validationStateValue = "valid";
  } else {
    validationStateValue = "invalid";
  }
  let errorMessageValue;
  if (isValid) {
    errorMessageValue = null;
  } else if (animalId === 2) {
    errorMessageValue = "The author of this example is a dog person.";
  } else {
    errorMessageValue = "Oh no it's a snake! Choose anything else.";
  }
  return (
    <Box sx={{ py: 4, fontSize: 3, maxWidth: "308px" }}>
      {domLoaded && (
        <AtlasSelect
          validationState={validationStateValue}
          label={"Favorite animal"}
          description={
            isValid ? "Pick your favorite animal, you will be judged." : null
          }
          errorMessage={"The author of this example is a dog person."}
          items={animalOptions}
          selectedKey={animalId}
          onSelect={(selected) => setAnimalId(selected)}
          isInvalid={!isValid}
        />
      )}
    </Box>
  );
};
